/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useRef, useState } from "react";
import SVG from "react-inlinesvg";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../Store/Hooks";
import { selectIsProviderLoggedIn } from "../../Store/Provider/selectors";
import VerifyEmail from "./Components/VerifyEmail";
import packageJson from '../../../package.json';
import CustomModal from "../../Components/Modals/CustomModal";
import RegisterForm from "./Components/RegisterForm";
import Loader from "../../Components/Helpers/Loaders/Loader";
import { initiateResetPassword, resetUserPassword, signIn, userAccountCheck } from "../../Store/Provider/ProviderAPI";
import { toastError, toastSuccess, toastWarning } from "../../Methods/Helpers/ToastHelper";
import { APIResponseType } from "../../Services/Models/Async/AsyncModel";
import { tempActions } from "../../Store/TempStore/reducer";
import { TempStore } from "../../Store/TempStore/types";
import { selectTempState } from "../../Store/TempStore/selectors";
import { providerActions } from "../../Store/Provider/reducer";

//form-control Error class=> form-control-lg is-invalid
const Register = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const tempState: TempStore = useAppSelector(selectTempState);
  const isLoggedIn: boolean = useAppSelector(selectIsProviderLoggedIn);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [email, setEmail] = useState<string | null>(null);
  const [password, setPassword] = useState<string | null>(null);
  const [isConfirmSignUp, setIsConfirmSignUp] = useState<boolean>(false);
  const [isVerifiedEmail, setIsVerifiedEmail] = useState<boolean>(false);
  const registerFormRef: any = useRef();

  useEffect(() => {
    if(!isVerifiedEmail && isConfirmSignUp){
      document.body.classList.add('backdroperbackbtn');
    } else {
      document.body.classList.remove('backdroperbackbtn');
    }
  }, [isVerifiedEmail , isConfirmSignUp]);

  useEffect(()=>{
    if(isLoggedIn){
      dispatch(tempActions.removeFromTemp("register"));
    }
  },[isLoggedIn, dispatch]);

  useEffect(() => {
    if(tempState?.register?.email){
      setEmail(tempState?.register?.username);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEmailPasswordSubmit = async (email : string, password: string) => {
    try {
      setIsLoading(true);
      const { success, code, message }: any = await userAccountCheck(email);
      switch (message){
        case "RESET_REQUIRED":
        case "USER_NOT_CONFIRMED":
        case "FORCE_CHANGE_PASSWORD":
            await initiateResetPassword(email).then((response: APIResponseType<any>) => {
              if(response.success){
                toastSuccess(response.message);
                setEmail(email);
                setPassword(password);
                setIsConfirmSignUp(true);
              }else{
                if(response.code === "LimitExceededException"){
                  toastError("Attempt limit exceeded, please try after some time.");
                }else{
                  toastError(response.message);
                }
              }
              setIsLoading(false);
            }).catch((error: APIResponseType<any>) => {
              toastError(error.message);
              setIsLoading(false);
            });
            break;
        case "USER_CONFIRMED" :
            toastWarning("Your account is already setup, please login to continue.");
            setTimeout(() => {
              navigate('/login');
            }, 2000)
            break;
        case "USER_NOT_REGISTERED" : 
          toastError("You are not registered, please contact Kiddo team or your management to onboard you at Kiddo Provider Portal");
          setIsLoading(false);
          break;
        default:
          toastError(message);
          setIsLoading(false);
      }
    } catch (error: any) {
      toastError(error.toString());
      setIsLoading(false);
    }
  }

  const confirmSignUp = async (otp: string) => {
    setIsLoading(true);
    await resetUserPassword({username: email!, password: password!, verificationCode: otp}).then((response: APIResponseType<any>) => {
      if(response.success){
        toastSuccess("Account setup successful");
        setIsVerifiedEmail(true);
        loginProvider(email!, password!);
      }else{
        toastError(response.message);
        setIsLoading(false);
      }
      handleVerifyPopupClose();
    }).catch((error: APIResponseType<any>)=>{
        toastError(error.message);
        handleVerifyPopupClose();
        setIsLoading(false);
    });
    registerFormRef.current.clearForm();
  }

  const resendVerificationCode = async () => {
    setIsLoading(true);
    await initiateResetPassword(email!).then((response: APIResponseType<any>) => {
      if(response.success){
        toastSuccess(response.message);
      }else{
        if(response.code === "LimitExceededException"){
          toastError("Attempt limit exceeded, please try after some time.");
        }else{
          toastError(response.message);
        }
      }
      setIsLoading(false);
    }).catch((error: APIResponseType<any>) => {
      toastError(error.message);
      setIsLoading(false);
    });
  }

  const loginProvider = async (email: string, password: string) => {
    setIsLoading(true);
    await signIn({email, password}).then((response: APIResponseType<any>) => {
      if(response.success){
        dispatch(providerActions.loginRequest({email, password}));
      }else{
        toastError(response.message);
        setIsLoading(false);
      }
    }).catch((error: APIResponseType<any>) => {
      toastError(error.message);
      setIsLoading(false);
    });
  }

  const handleVerifyPopupClose = () => {
    setIsConfirmSignUp(false);
    registerFormRef.current.clearForm();
  }

  return (
    <>
      {/* {isLoggedIn && <Navigate to={"/account-setup-complete"} />} */}
      {isLoggedIn && <Navigate to={"/addpatientleads"} />}
      <div className="main-wrapper onboarding-screens">
        <div className="cards-wrap card-success">
          <div className="card-body onboarding-model">
            <div className={`header ${isLoggedIn ? "header-full-text" : ""}`}>
              <div className="logo mb-4">
                <SVG
                  className="header-logo"
                  src="Assets/Images/Logos/kiddo.svg"
                  width="108"
                  height="32"
                />
              </div>
              <h1 className="title-medium text-center">Account Setup</h1>
              <p className="header-text text-start mb-4">Understanding the whole child to improve their care.</p>
            </div>
            <div className="card-inner create-account position-relative">
              <RegisterForm ref={registerFormRef} onSubmitDetails={handleEmailPasswordSubmit} />
              <div className="text-center mt-2">
                <p className="theme-color">
                  Have an account?{" "}
                  <Link to={"/login"} className="theme-color">
                    Login
                  </Link>{" "}
                  here.
                </p>
              </div>
              <div className="text-center">
                <p className="theme-color">
                  &#169; Kiddo Health Inc. | Version: {packageJson.version}
                </p>
              </div>
            </div>
            {!isVerifiedEmail && isConfirmSignUp && 
              <CustomModal
                show={isConfirmSignUp}
                BodyContent={() => (
                  <>
                      <div className="header w-100 justify-content-center">
                        <div className="text-center">
                          <div className="logo mb-4 text-center">
                            <SVG
                              className="header-logo"
                              src="Assets/Images/Logos/kiddo.svg"
                              width="108"
                              height="32"
                            />
                          </div>
                          <h1 className="title-medium text-center theme-color fs-5 mb-3">Email Verification</h1>
                          <p className="header-text mb-5">
                            {`Verification code has been sent to ${tempState?.register?.email}`}
                          </p>
                        </div>
                      </div>
                      <VerifyEmail
                        onResendOTP={resendVerificationCode}
                        onSubmitOTP={confirmSignUp}
                        isInitialSendCode={true}
                        loading={isLoading}
                      />
                  </>
                  
                )}
                closeButton={true}
                CloseContent={handleVerifyPopupClose}
                customClass="verify-otp-padding"
              />
            }
            {isLoading && <Loader />}
          </div>
        </div>
      </div>
    </>
  );
};

export default Register;
